import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  CheckIcon,
  ConfirmModal,
  Form,
  Heading,
  Label,
  LoadableContentContainer,
  ScrollableContainer,
  TextField,
  UseFormReturn,
  WarningIcon,
  XmarkIcon,
} from "@wexinc/wex-ui";
import classNames from "classnames";
import { useRef, useState } from "react";
import { FieldValues, FormState } from "react-hook-form";
import useCOPOrganizationService, {
  AddMemberResponse,
  OrganizationInvitation,
  OrganizationInviteStatus,
} from "services/organization";
import * as yup from "yup";

export type onMembersAddedHandler = (
  organizationId: string,
  addMemberResponses: AddMemberResponse<OrganizationInvitation>[],
) => void;

interface Props {
  organizationId: string;
  canEditRoles: boolean;
  canAssignOrgOwnerRole: boolean;
  onMembersAdded: onMembersAddedHandler;
  onCancel: () => void;
}

interface AddMemberSchema {
  email: string;
  confirmEmail: string;
}

const validationSchema = yup.object<AddMemberSchema>({
  email: yup
    .string()
    .email("Must be a valid email.")
    .trim()
    .required("Email is required.")
    .max(100, "Email cannot exceed 100 characters"),
  confirmEmail: yup
    .string()
    .email("Must be a valid email.")
    .trim()
    .required("Confirm email is required.")
    .max(100, "Email cannot exceed 100 characters")
    .oneOf([yup.ref("email")], "Emails must match"),
});

interface AddMemberInfo {
  status?: OrganizationInviteStatus;
  statusDescription?: string;
}

const AddOrganizationMembersModal: React.FC<Props> = (props) => {
  const copOrganizationService = useCOPOrganizationService();
  const [emailAndStatuses, setEmailAndStatuses] = useState<Map<string, AddMemberInfo>>(new Map());
  const useFormReturn = useRef<UseFormReturn>();
  const { organizationId, onCancel, onMembersAdded } = props;
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const addMemberResponsesSaved = useRef<AddMemberResponse<OrganizationInvitation>[]>();

  function onFormInit(useFormReturnIncoming: UseFormReturn) {
    useFormReturn.current = useFormReturnIncoming;
  }

  const key = 0;
  const value = 1;

  async function onSave() {
    setSaving(true);
    const addMemberResponses = await copOrganizationService.addOrganizationMembers(organizationId, [
      ...emailAndStatuses.keys(),
    ]);

    addMemberResponses.forEach((i) => {
      let statusDescription = "";
      switch (i.status) {
        case "ADDED":
          statusDescription = "New member added.";
          break;
        case "ALREADY_ADDED":
          statusDescription = "Already in this organization.";
          break;
        case "ALREADY_INVITED":
          statusDescription = "Has already been invited to this organization.";
          break;
        case "INVITATION_SENT":
          statusDescription = "Invitation sent.";
          break;
        case "DEACTIVATED_USER":
          statusDescription = "User is deactivated and cannot be added.";
          break;
        case "ERROR":
          statusDescription = i.error!.errors.map((e) => e.message).join(". ");
          break;
      }
      emailAndStatuses.set(i.email, { status: i.status, statusDescription });
    });
    setEmailAndStatuses(new Map([...emailAndStatuses]));
    addMemberResponsesSaved.current = addMemberResponses;
    setSaving(false);
    setSubmitted(true);
  }

  function afterConfirmingErrors() {
    setSaving(true);
    onMembersAdded(organizationId, addMemberResponsesSaved.current!);
  }

  function stageMember({ email }: { email: string }) {
    email = email.toLowerCase();
    if (!emailAndStatuses.has(email)) {
      emailAndStatuses.set(email, {});
      setEmailAndStatuses(new Map([...emailAndStatuses]));
    }
    setTimeout(() => {
      useFormReturn.current?.reset();
    }, 0);
  }

  function removeStagedMember(email: string) {
    emailAndStatuses.delete(email);
    setEmailAndStatuses(new Map([...emailAndStatuses]));
  }

  function onRealtimeChanges(data: FieldValues, formState: FormState<FieldValues>) {
    if (formState.isSubmitted && data["email"] && data["confirmEmail"]) {
      useFormReturn.current?.trigger(["email", "confirmEmail"]);
    }
  }

  return (
    <ConfirmModal
      onOk={submitted ? afterConfirmingErrors : onSave}
      okButtonDisabled={emailAndStatuses.size === 0}
      showCancelButton={submitted ? false : true}
      onCancel={onCancel}
      okButtonText={submitted ? "OK" : "Save"}
      contentClassNames="max-h-72 min-h-[180px]"
      explicitOpenClose={true}
      modalClassNames="!max-w-2xl"
    >
      <LoadableContentContainer
        showLoading={saving}
        message="Saving..."
      />
      <Heading
        size="small"
        className="mb-6"
      >
        Add Org Members
      </Heading>

      <div>
        <Form
          className="flex items-center mb-6"
          onSubmit={stageMember}
          options={{ resolver: yupResolver(validationSchema) }}
          onInit={onFormInit}
          onRealtimeChanges={onRealtimeChanges}
        >
          <div className="flex items-center rounded bg-primary-10 p-4 border border-primary-20">
            <TextField
              optOutOfCopy
              readonly={submitted}
              name="email"
              placeholder="Email"
              autocomplete={false}
              containerClassName="shrink-0 !w-64 mr-4 !mb-0"
            ></TextField>
            <TextField
              readonly={submitted}
              name="confirmEmail"
              placeholder="Confirm email"
              autocomplete={false}
              containerClassName="shrink-0 !w-64 mr-4 !mb-0"
            ></TextField>
            <Button
              disabled={submitted}
              type="submit"
              testId="stage-member"
              buttonType="secondary-neutral"
            >
              Add
            </Button>
          </div>
        </Form>

        {emailAndStatuses.size > 0 && (
          <ScrollableContainer className="max-h-40">
            <ul>
              {[...emailAndStatuses].map((i) => (
                <li
                  key={i[key]}
                  className="flex pb-3 justify-between overflow-hidden"
                >
                  <div className="flex items-center">
                    {!submitted && (
                      <Button
                        allowFocusStyling={false}
                        buttonType="secondary-critical"
                        testId="remove-staged-member"
                        size="small"
                        onClick={() => removeStagedMember(i[key])}
                        className="mr-2 bg-utility-transparent"
                      >
                        <XmarkIcon />
                      </Button>
                    )}
                    {(i[value].status === "ADDED" || i[value].status === "INVITATION_SENT") && (
                      <CheckIcon className="mr-2 text-utility-success-50 fill-current" />
                    )}
                    {(i[value].status === "ALREADY_ADDED" ||
                      i[value].status === "ALREADY_INVITED" ||
                      i[value].status === "DEACTIVATED_USER" ||
                      i[value].status === "ERROR") && (
                      <WarningIcon
                        className={classNames(
                          "mr-2 fill-current",
                          i[value].status === "ERROR" ? "text-utility-critical-50" : "text-utility-warning-50",
                        )}
                      />
                    )}
                    <Label
                      size="base"
                      className="mr-2"
                    >
                      {i[key]}
                      {submitted ? " - " : ""}
                    </Label>
                    <Label size="base">{i[value].statusDescription}</Label>
                  </div>
                </li>
              ))}
            </ul>
          </ScrollableContainer>
        )}
      </div>
    </ConfirmModal>
  );
};

export default AddOrganizationMembersModal;
